import { useState, useMemo } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import KpiAchievementContribution, {
  type KpiAchievementContributionData,
  type Order,
} from '../../stories/KpiAchievementContribution/KpiAchievementContribution'
import { useWidgetContext } from '../../widgets/providers'
import { useAllTeams } from '../../teams/api'
import { useData, getMaxPeriod } from '../../widgets/api'
import { useAllPlayers } from '../../players/api'

export default function PerformanceKpiAchievementContribution() {
  const ctx = useWidgetContext()
  const metricKey = ctx.metric || 'revenue'
  const subject_type = ctx.subject_type || 'team'

  const [order, setOrder] = useState<Order>({
    field: 'contribution',
    direction: 'asc',
  })

  const { data: teams } = useAllTeams({
    competition_id: ctx.competition_id || 0,
  })
  const { data: players } = useAllPlayers({
    competition_id: ctx.competition_id || 0,
  })

  const subjects =
    subject_type === 'team'
      ? teams.items.map((t) => ({
          id: t.team_id,
          name: t.name,
          logo: t.logo.thumbnail,
        }))
      : players.items.map((p) => ({
          id: p.user_id || 0,
          name: p.name,
          logo: p.photo.thumbnail,
        }))

  const subject_ids =
    subject_type === 'team'
      ? teams.items.map((t) => t.team_id)
      : players.items.map((p) => p.user_id || 0)

  // Get max year period
  const { data: maxYearData } = useSuspenseQuery({
    queryKey: ['max-year-period', subject_type, ctx.competition_id],
    queryFn: () =>
      getMaxPeriod({
        period_type: 'year',
        subject_type,
        competition_id: ctx.competition_id,
      }),
  })

  const maxYearPeriod = maxYearData?.max_period || dayjs().format('YYYY-MM-DD')

  // Fetch all data in one query: percent+value, percent+rank, actual+value, actual+rank
  const { data: metricsData } = useData({
    period_type: 'year',
    period_range: false,
    periods: [maxYearPeriod],
    subject_type,
    subject_ids,
    metrics: [metricKey, metricKey, metricKey, metricKey],
    components: ['percent', 'percent', 'actual', 'actual'],
    statistics: ['value', 'rank', 'value', 'rank'],
    competition_id: ctx.competition_id,
  })

  // Transform data for the KpiAchievementContribution component
  const listData = useMemo(() => {
    // Create maps for values and ranks using decoded component/statistic strings
    const percentValueMap = new Map<number, number>()
    const percentRankMap = new Map<number, number>()
    const actualValueMap = new Map<number, number>()
    const actualRankMap = new Map<number, number>()

    metricsData?.forEach((d) => {
      if (d.component === 'percent' && d.statistic === 'value') {
        percentValueMap.set(d.subject, d.value)
      } else if (d.component === 'percent' && d.statistic === 'rank') {
        percentRankMap.set(d.subject, d.value)
      } else if (d.component === 'actual' && d.statistic === 'value') {
        actualValueMap.set(d.subject, d.value)
      } else if (d.component === 'actual' && d.statistic === 'rank') {
        actualRankMap.set(d.subject, d.value)
      }
    })

    // Find max contribution to calculate relative percentages
    const maxContribution = Math.max(
      ...subjects.map((s) => actualValueMap.get(s.id) || 0)
    )

    // Build data with achievement, contribution, and ranks from API
    return subjects.map(
      (subject): KpiAchievementContributionData => ({
        metric: metricKey,
        achievement: (percentValueMap.get(subject.id) || 0) * 100,
        contribution: actualValueMap.get(subject.id) || 0,
        contribution_percent:
          maxContribution > 0
            ? (actualValueMap.get(subject.id) || 0) / maxContribution
            : 0,
        achievement_rank: percentRankMap.get(subject.id) || 0,
        contribution_rank: actualRankMap.get(subject.id) || 0,
        team_name: subject.name,
        team_logo: subject.logo,
      })
    )
  }, [subjects, metricsData, metricKey])

  // Sort data based on order
  const sortedData = useMemo(() => {
    const field =
      order.field === 'achievement' ? 'achievement_rank' : 'contribution_rank'
    // Missing ranks are coerced to 0 above; treat them as unranked so they
    // always sort to the bottom regardless of direction.
    const worst =
      order.direction === 'asc'
        ? Number.POSITIVE_INFINITY
        : Number.NEGATIVE_INFINITY
    const rankFor = (r: number) => (r > 0 ? r : worst)
    return [...listData].sort((a, b) => {
      const av = rankFor(a[field])
      const bv = rankFor(b[field])
      if (av === bv) return 0
      return order.direction === 'asc' ? (av < bv ? -1 : 1) : av > bv ? -1 : 1
    })
  }, [listData, order])

  return (
    <div>
      <KpiAchievementContribution
        data={sortedData}
        order={order}
        onOrderChange={setOrder}
        isPlayer={subject_type === 'user'}
      />
    </div>
  )
}
